/**
 * OnboardingActions — server-driven onboarding action chips (v2).
 *
 * Reads ONBOARDING_CHIP_ACTIONS from @xavior/contracts to determine which
 * chips to render for the current onboardingState. Delegates all side-effect
 * logic (OAuth popup, demo load, skip, letter callbacks) to the parent.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ONBOARDING_CHIP_ACTIONS,
  type OnboardingState,
  type OnboardingChipKind,
} from '@xavior/contracts';
import { listProviders } from '@/lib/api/endpoints/integrations';
import { api } from '@/lib/api';

export interface OnboardingActionsProps {
  onboardingState: OnboardingState | null | undefined;
  token: string | null | undefined;
  /** When true, the agent is still streaming — hide chips until done */
  isStreaming?: boolean;
  onOAuthPopup: (kind: 'oauth.google.email' | 'oauth.microsoft.outlook') => Promise<void>;
  onDemoLoad: () => Promise<void>;
  onAdvance?: (to: OnboardingState) => Promise<void>;
  onOpenPage?: (parentPagesId: string, childModuleId: string) => void;
  onStatusMessage?: (text: string) => void;
  onSendSuggestion?: (message: string) => void;
}

const SUGGESTION_CHIPS = [
  { label: 'Analyze my inbox', message: 'Analyze my inbox and tell me what needs my attention most.' },
  { label: 'Show my top contacts', message: 'Who are my top 5 contacts based on my email?' },
];

export function OnboardingActions({
  onboardingState,
  token,
  isStreaming,
  onOAuthPopup,
  onDemoLoad,
  onAdvance,
  onOpenPage,
  onStatusMessage,
  onSendSuggestion,
}: OnboardingActionsProps) {
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [briefingPhase, setBriefingPhase] = useState(false);
  const [suggestionPhase, setSuggestionPhase] = useState(false);
  // Synchronous guard: useState is not StrictMode-safe for click dedup
  const inFlightRef = useRef(false);
  // Default to null so we show the Microsoft chip while loading (avoid flash-of-missing)
  const [microsoftAvailable, setMicrosoftAvailable] = useState<boolean | null>(null);

  // Fetch provider list when email chips are shown to determine if Microsoft chip should render
  useEffect(() => {
    if (onboardingState !== 'email_pitched' && onboardingState !== 'pending') return;

    let cancelled = false;
    listProviders(token ?? null)
      .then((items) => {
        if (cancelled) return;
        setMicrosoftAvailable(
          items.some((p) => p.id === 'microsoft' && p.hasServerCredentials !== false),
        );
      })
      .catch(() => {
        // On error, leave microsoftAvailable as null (safe default: show the chip)
      });

    return () => {
      cancelled = true;
    };
  }, [onboardingState, token]);

  const handleChip = useCallback(async (kind: OnboardingChipKind) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      // If paywall was skipped (local dev) the state may still be 'pending'.
      // Silently advance to email_pitched first — this transition is always
      // valid in the API, putting the state machine in the right place before
      // email_connected / email_skipped transitions are attempted.
      if (onboardingState === 'pending') {
        await onAdvance?.('email_pitched');
      }
      if (kind === 'oauth.google.email' || kind === 'oauth.microsoft.outlook') {
        await onOAuthPopup(kind);
      } else if (kind === 'demo.load') {
        await onDemoLoad();
      } else if (kind === 'skip') {
        await onAdvance?.('email_skipped');
      } else if (kind === 'letter.generate') {
        onStatusMessage?.('Writing your letter — this takes about 8 seconds...');
        try {
          const data = await api.selfLetter.generateSelfLetter(token ?? null, { entryPoint: 'onboarding' });
          if (data.ok && data.parentPagesId && data.pageId) {
            onOpenPage?.(data.parentPagesId, data.pageId);
            onStatusMessage?.('Your letter is ready — take a look. Would you like me to send you a morning briefing each day? I can pull together your inbox highlights, open tasks, and calendar so you start every day already caught up.');
            setBriefingPhase(true);
          } else {
            onStatusMessage?.("I ran into a problem writing the letter. You can ask me for one anytime.");
          }
        } catch {
          onStatusMessage?.("I ran into a problem writing the letter. You can ask me for one anytime.");
        }
        // No state transition here — briefing chips are shown locally via briefingPhase
        // to avoid triggering the agent via the directive/BOOTSTRAP system.
      } else if (kind === 'letter.decline') {
        setDismissed(true);
        onStatusMessage?.("No problem — you can ask me for one anytime.");
        await onAdvance?.('completed');
      } else if (kind === 'briefing.enable') {
        try {
          await api.onboarding.setBriefingPreference(token ?? null, true);
        } catch { /* best-effort */ }
        onStatusMessage?.("Done! I'll send you a morning briefing each day — inbox highlights, calendar, and open tasks. You can update the time in Settings.");
        setBriefingPhase(false);
        setSuggestionPhase(true);
        await onAdvance?.('completed');
      } else if (kind === 'briefing.skip') {
        setBriefingPhase(false);
        setSuggestionPhase(true);
        await onAdvance?.('completed');
      }
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [onboardingState, onOAuthPopup, onDemoLoad, onAdvance, onOpenPage, onStatusMessage, token]);

  if (dismissed) return null;

  // Briefing phase is handled locally — no state transition — chips are
  // hardcoded here to avoid coupling this UI-only flow to the state machine.
  if (briefingPhase && !loading) {
    return (
      <div className="onboarding-actions">
        <div className="oa-primary-group">
          <button className="oa-chip oa-chip--primary" onClick={() => void handleChip('briefing.enable')}>
            <span className="oa-chip__text"><span className="oa-chip__label">Yes, set it up</span></span>
          </button>
        </div>
        <div className="oa-secondary-group">
          <button className="oa-chip oa-chip--secondary" onClick={() => void handleChip('briefing.skip')}>
            Not right now
          </button>
        </div>
      </div>
    );
  }

  // Post-onboarding: show suggestion chips to get the user started.
  if (suggestionPhase && !loading) {
    return (
      <div className="onboarding-actions">
        <div className="oa-secondary-group">
          {SUGGESTION_CHIPS.map((chip) => (
            <button
              key={chip.label}
              className="oa-chip oa-chip--secondary"
              onClick={() => {
                onSendSuggestion?.(chip.message);
                setDismissed(true);
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Return null for states that don't have chips
  if (
    !onboardingState ||
    !(onboardingState in ONBOARDING_CHIP_ACTIONS)
  ) {
    return null;
  }

  // Wait for the agent to finish streaming before showing chips
  if (isStreaming) {
    return null;
  }

  const chips = ONBOARDING_CHIP_ACTIONS[onboardingState] ?? [];

  // Filter out microsoftOnly chips when Microsoft is unavailable.
  // null means "still loading" — treat same as true (show the chip).
  const visibleChips = chips.filter(
    (chip) => !chip.microsoftOnly || microsoftAvailable !== false,
  );

  if (visibleChips.length === 0) return null;

  // Hide buttons entirely while a chip action is in progress
  if (loading) return null;

  const primaryChips = visibleChips.filter((c) => c.variant === 'primary');
  const secondaryChips = visibleChips.filter((c) => c.variant !== 'primary');

  const renderIcon = (chip: typeof visibleChips[0]) => {
    if (chip.icon === 'google') return (
      <svg className="oa-chip__icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09a7.12 7.12 0 0 1 0-4.17V7.07H2.18a11.99 11.99 0 0 0 0 9.86l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.16 6.16-4.16z" fill="#EA4335"/>
      </svg>
    );
    if (chip.icon === 'microsoft') return (
      <svg className="oa-chip__icon" viewBox="0 0 23 23" width="20" height="20" aria-hidden="true">
        <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
        <rect x="12" y="1" width="10" height="10" fill="#7fba00"/>
        <rect x="1" y="12" width="10" height="10" fill="#00a4ef"/>
        <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
      </svg>
    );
    return null;
  };

  // The welcome / ask / connect beats now live in OnboardingBeats (rendered as a
  // persisted chat transcript), so this component no longer draws the pre-connect
  // intro — suppress the data-source chips for that state.
  const sourceChips = visibleChips.filter(
    (c) => c.kind === 'oauth.google.email' || c.kind === 'oauth.microsoft.outlook' || c.kind === 'demo.load',
  );
  if (sourceChips.length > 0) return null;

  return (
    <div className="onboarding-actions">
      {primaryChips.length > 0 && (
        <div className="oa-primary-group">
          {primaryChips.map((chip) => (
            <button
              key={chip.kind}
              className="oa-chip oa-chip--primary"
              disabled={loading}
              onClick={() => void handleChip(chip.kind)}
            >
              {renderIcon(chip)}
              <span className="oa-chip__text">
                <span className="oa-chip__label">{chip.label}</span>
                {chip.sublabel && <span className="oa-chip__sublabel">{chip.sublabel}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
      {secondaryChips.length > 0 && (
        <div className="oa-secondary-group">
          {secondaryChips.map((chip) => (
            <button
              key={chip.kind}
              className="oa-chip oa-chip--secondary"
              disabled={loading}
              onClick={() => void handleChip(chip.kind)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
